/**
 * use-module-list-filter-options.ts — opcje wiersza filtrów listy kartoteki własnej, które
 * trzeba wczytać (nie wynikają z definicji pola): użytkownicy dla pól „użytkownik”, rekordy
 * celów relacji dla pól „relacja” i pozycje słowników dla pól wyboru ze słownikiem (patrz
 * `moduleFieldFilter` w `module-list-columns.tsx`). Hak jest wspólny dla strony listy
 * kartoteki i sekcji kokpitu decyzji — różni je tylko adres podpowiedzi wartości relacji.
 *
 *  - Użytkownicy: `GET /users?limit=200` (ten sam klucz cache i etykiety co „Opiekun” na
 *    listach systemowych — wspólny hak `useUserFilterOptions`: nazwa wyświetlana, a bez niej
 *    login, NIGDY e-mail). Zapytanie idzie tylko, gdy lista ma pole „użytkownik” i użytkownik ma
 *    prawo podglądu użytkowników (`crm_core.users.view`); bez prawa albo przy odmowie 403 opcje są
 *    puste, a kolumna zostaje bez kontrolki filtra (pusta komórka); każdy inny błąd pobrania
 *    kończy się wpisem `console.error`. LIMIT: tenant z ponad 200 użytkownikami zobaczy w filtrze
 *    pierwszych 200 (znany limit, jak przy „Opiekunie”).
 *  - Relacje: adres podpowiedzi podaje wołający (`relationDistinctUrl`) — serwer zwraca dla pól
 *    relacji `values[]` z `value` = id rekordu i `label` = nazwa rekordu (liczone z rekordów,
 *    które faktycznie mają wartość w tym polu). Strona listy pyta o `GET /modules/:id/records/
 *    distinct?field=<slug>` (rekordy całej kartoteki), sekcja kokpitu — o `GET /processes/
 *    my-pending-decisions/section/distinct?key=custom.<slug>&field=<slug>` (rekordy SAMEJ
 *    sekcji, z zakresem „Przeterminowane” / „Zespół”). Klucz cache `['distinct-values', tableId,
 *    slug, adres]` zaczyna się jak w popupie lejka w nagłówku (`useDistinctValues`), więc
 *    unieważnienia podpowiedzi po prefiksie `['distinct-values', tableId]` (po usunięciu
 *    rekordów, po decyzji w sekcji) obejmują także te zapytania; adres w kluczu odróżnia
 *    zapytania o to samo pole spod różnych adresów. `tableId` to identyfikator podpowiedzi tabeli
 *    (`distinctTableId`, np. `module-<id>` BEZ sufiksu układu wymuszonego z menu), nie klucz
 *    układu listy. Błąd pobrania (poza 403) kończy się wpisem `console.error`, raz na pole.
 *    LIMIT: serwer zwraca najwyżej 500 różnych wartości (`truncated: true` powyżej) — pole
 *    z większą liczbą rekordów dostaje w filtrze tyle wartości, ile przyszło w odpowiedzi (w
 *    trybie deweloperskim zgłaszane ostrzeżeniem z tą liczbą, raz na pole). Opcje sortowane wg
 *    języka interfejsu.
 *  - Słowniki: pole `select` z `dictionaryCode` i pole innego typu wyboru (radio, status, lista
 *    wielokrotna, grupa checkboxów) BEZ opcji w definicji, ale z `dictionaryCode` — o ile pole nie
 *    ma wiązań parametrów słownika zewnętrznego (te zależą od wartości rekordu, więc statycznej
 *    listy nie ma) — dostaje listę z pozycjami słownika: wartość = kod pozycji (to, co rekord
 *    trzyma w kolumnie), etykieta wg trybu wyświetlania pola, a bez niego — słownika. W polu
 *    `select` pozycje słownika wygrywają z opcjami z definicji, jak w formularzu rekordu
 *    (`SelectFieldType`); w pozostałych typach pierwszeństwo mają opcje z definicji. Zapytanie to
 *    samo i ten sam klucz cache co `useDictionary` / filtry list systemowych (wspólny hak
 *    `useDictionaryDetailsByCode`); trasa nie ma bramki uprawnień. Dopóki słownik się wczytuje,
 *    pole jest na liście `dictionaryPendingSlugs` (komórka filtra pusta); słownik niedostępny
 *    (zgłaszany w logu jak w `useDictionaryDetailsByCode`) — kolumna zostaje przy filtrze
 *    tekstowym po zapisanej wartości, jak dotąd (nie przy opcjach z definicji: formularz pola ze
 *    słownikiem ich nie zapisuje).
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';

import {
  DISTINCT_VALUES_QUERY_KEY,
  DISTINCT_VALUES_STALE_TIME_MS,
  type DistinctValue,
  type DistinctValuesResponse,
} from '@/components/configurable-table/useDistinctValues';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { type DictionaryDetail, hasParamBindings } from '@/hooks/useDictionary';
import { api } from '@/lib/api';
import type { ListFilterOption } from '@/lib/ext-list-columns';
import {
  dictionaryFilterOptions,
  useDictionaryDetailsByCode,
  useUserFilterOptions,
} from '@/lib/list-filter-options';
import { isForbiddenError } from '@/lib/query';

import {
  fieldHasFilterOptions,
  type ModuleListFilterOptions,
  relationFilterOptionsFromDistinct,
} from './module-list-columns';

/**
 * Typy pól wyboru, dla których opcje filtra mogą pochodzić ze słownika. Bez tagów: rekord trzyma
 * w nich tekst tagu, nie kod pozycji, więc lista kodów nie pasowałaby do zapisanych wartości.
 */
const DICTIONARY_FILTER_FIELD_TYPES: ReadonlySet<string> = new Set([
  'select',
  'radio',
  'status',
  'multiselect',
  'checkboxgroup',
]);

/** Stan wczytania podpowiedzi jednego pola „relacja” — same dane potrzebne hakowi. */
interface RelationLoadState {
  values: ReadonlyArray<DistinctValue> | undefined;
  /** Odpowiedź ucięta limitem serwera (`truncated: true`). */
  truncated: boolean;
  error: Error | null;
}

/** Zbiera same dane zapytań o relacje (stabilna referencja `combine`). */
function combineRelationStates(
  results: UseQueryResult<DistinctValuesResponse, Error>[],
): RelationLoadState[] {
  return results.map((result) => ({
    values: Array.isArray(result.data?.values) ? result.data.values : undefined,
    truncated: result.data?.truncated === true,
    error: result.error,
  }));
}

// Strażnik błędów wczytania relacji (deduplikowany po `tableId:slug`): błąd bez śladu zostawiałby
// kolumnę bez kontrolki filtra i bez wyjaśnienia. Odmowa 403 to brak prawa, nie awaria — bez logu.
const loggedRelationLoadFailures = new Set<string>();
function logRelationLoadFailure(tableId: string, slug: string, error: Error): void {
  if (isForbiddenError(error)) return;
  const key = `${tableId}:${slug}`;
  if (loggedRelationLoadFailures.has(key)) return;
  loggedRelationLoadFailures.add(key);
  console.error(
    `[useModuleListFilterOptions] Nie wczytano opcji filtra „relacja” pola „${slug}” ` +
      `(${error.message}) — kolumna zostaje bez listy wyboru:`,
    error,
  );
}

// Dev-only strażnik (deduplikowany po `tableId:slug`) — lista ucięta limitem serwera (`count` =
// liczba wartości, które przyszły w odpowiedzi). Milczy w produkcji (`import.meta.env.DEV`).
const warnedRelationTruncations = new Set<string>();
function devWarnRelationTruncated(tableId: string, slug: string, count: number): void {
  const key = `${tableId}:${slug}`;
  if (!import.meta.env.DEV || warnedRelationTruncations.has(key)) return;
  warnedRelationTruncations.add(key);
  console.warn(
    `[useModuleListFilterOptions] Lista rekordów pola „${slug}” w filtrze jest ucięta limitem ` +
      `serwera (${count} wartości) — rekordy spoza niej nie mają opcji w wierszu filtrów.`,
  );
}

/** Pole wyboru ze słownikiem — to, co hak musi wiedzieć, żeby zbudować jego opcje filtra. */
interface DictionaryFieldEntry {
  slug: string;
  code: string;
  /** Nadpisanie trybu wyświetlania pozycji dla tego pola (`null` = tryb słownika). */
  displayMode: DictionaryDetail['displayMode'] | null;
}

/**
 * Kod słownika, z którego idą opcje filtra pola wyboru; `null` = pole nie korzysta ze słownika
 * (nie jest polem wyboru, nie ma `dictionaryCode`, słownik jest zewnętrzny z wiązaniami
 * parametrów — jego lista zależy od wartości rekordu — albo pole inne niż `select` ma użyteczne
 * opcje w definicji). Pole `select` ze słownikiem ZAWSZE bierze pozycje słownika, także gdy ma
 * opcje w definicji: formularz rekordu (`SelectFieldType`) pokazuje i zapisuje wtedy pozycje
 * słownika (wartość = kod), więc filtr po opcjach z definicji nie pasowałby do zapisanych wartości.
 * W pozostałych typach „ma opcje w definicji” rozstrzyga ten sam predykat co filtr wiersza
 * (`fieldHasFilterOptions`).
 */
export function fieldDictionaryCode(field: ModuleField): string | null {
  if (!DICTIONARY_FILTER_FIELD_TYPES.has(field.fieldType)) return null;
  if (hasParamBindings(field.dictionaryParamBindings)) return null;
  const code = field.dictionaryCode?.trim();
  if (!code) return null;
  if (field.fieldType !== 'select' && fieldHasFilterOptions(field)) return null;
  return code;
}

/** Pola wyboru ze słownikiem spośród pól listy (kolejność jak w `fields`). */
function dictionaryEntriesOf(fields: ReadonlyArray<ModuleField>): DictionaryFieldEntry[] {
  return fields.flatMap((field): DictionaryFieldEntry[] => {
    const code = fieldDictionaryCode(field);
    return code === null
      ? []
      : [{ slug: field.slug, code, displayMode: field.dictionaryDisplayMode ?? null }];
  });
}

export interface UseModuleListFilterOptionsArgs {
  /**
   * Identyfikator podpowiedzi tabeli (`distinctTableId`) — część klucza cache podpowiedzi (patrz
   * nagłówek pliku); na liście kartoteki `module-<id>`, bez sufiksu układu wymuszonego z menu.
   */
  tableId: string;
  /** Pola, dla których lista pokaże kolumny — z nich wynika, co wczytać. */
  fields: ReadonlyArray<ModuleField>;
  /**
   * Adres podpowiedzi wartości pola „relacja” (patrz nagłówek pliku); `null` = zapytania nie ma
   * (np. moduł jeszcze niewczytany). Funkcja nie musi być stabilna — hak czyta ją przy budowie
   * zapytań, nie trzyma w zależnościach.
   */
  relationDistinctUrl: (slug: string) => string | null;
}

/**
 * Wczytuje opcje filtra użytkowników, relacji i słowników dla listy kartoteki własnej. Wynik ma
 * stabilną tożsamość, dopóki nie zmienią się dane — można go wpisać do zależności `useMemo` kolumn.
 */
export function useModuleListFilterOptions({
  tableId,
  fields,
  relationDistinctUrl,
}: UseModuleListFilterOptionsArgs): ModuleListFilterOptions {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const hasUserField = fields.some((field) => field.fieldType === 'user');
  // Slugi pól mają format `^[a-z][a-z0-9_]*$`, więc `|` jest bezpiecznym separatorem; napis
  // daje stabilny klucz memoizacji (lista `fields` strony ma nową tożsamość co render).
  const relationKey = fields
    .filter((field) => field.fieldType === 'relation')
    .map((field) => field.slug)
    .join('|');
  const relationSlugs = useMemo(
    () => (relationKey === '' ? [] : relationKey.split('|')),
    [relationKey],
  );
  // Pola wyboru ze słownikiem — jak wyżej: stabilny klucz napisowy zamiast tożsamości listy pól.
  const dictionaryEntries = dictionaryEntriesOf(fields);
  const dictionaryKey = JSON.stringify(dictionaryEntries);
  const dictionaryFields = useMemo(
    () => dictionaryEntries,
    // Klucz napisowy zastępuje tożsamość listy: treść wpisów jest z niego wprost wyliczona.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dictionaryKey],
  );

  // Użytkownicy: bez prawa podglądu (albo bez pola „użytkownik”) zapytania nie wysyłamy.
  const userOptions = useUserFilterOptions({
    enabled: hasUserField,
    hookName: 'useModuleListFilterOptions',
    filterLabel: 'użytkownik',
  });

  const relationStates = useQueries({
    queries: relationSlugs.map((slug) => {
      const url = relationDistinctUrl(slug);
      return {
        queryKey: [DISTINCT_VALUES_QUERY_KEY, tableId, slug, url] as const,
        queryFn: async () => {
          if (url === null) throw new Error(`Brak adresu podpowiedzi pola „${slug}”`);
          const res = await api.get<DistinctValuesResponse>(url);
          return res.data;
        },
        enabled: url !== null,
        staleTime: DISTINCT_VALUES_STALE_TIME_MS,
      };
    }),
    combine: combineRelationStates,
  });
  // Błędy wczytania i ucięcie listy limitem serwera — jawnie, raz na pole (patrz strażniki wyżej).
  useEffect(() => {
    relationSlugs.forEach((slug, index) => {
      const state = relationStates[index];
      if (state?.error) logRelationLoadFailure(tableId, slug, state.error);
      if (state?.truncated && state.values) {
        devWarnRelationTruncated(tableId, slug, state.values.length);
      }
    });
  }, [tableId, relationSlugs, relationStates]);
  const relationOptions = useMemo(() => {
    const bySlug: Record<string, ReadonlyArray<ListFilterOption>> = {};
    relationSlugs.forEach((slug, index) => {
      const values = relationStates[index]?.values;
      if (!values) return;
      const options = relationFilterOptionsFromDistinct(values, locale);
      if (options.length > 0) bySlug[slug] = options;
    });
    return bySlug;
  }, [relationSlugs, relationStates, locale]);

  const dictionaryCodes = useMemo(
    () => [...new Set(dictionaryFields.map((entry) => entry.code))].sort(),
    [dictionaryFields],
  );
  const dictionaries = useDictionaryDetailsByCode(
    dictionaryCodes,
    'useModuleListFilterOptions',
    'pola wyboru z tym słownikiem zostają przy filtrze tekstowym w wierszu filtrów.',
  );
  const dictionaryState = useMemo(() => {
    const bySlug: Record<string, ReadonlyArray<ListFilterOption>> = {};
    const pendingSlugs = new Set<string>();
    for (const entry of dictionaryFields) {
      const detail = dictionaries.loaded.get(entry.code);
      if (!detail) {
        if (dictionaries.pending.has(entry.code)) pendingSlugs.add(entry.slug);
        continue;
      }
      // Tryb wyświetlania pola ma pierwszeństwo przed trybem słownika (jak w formularzu rekordu).
      const options = dictionaryFilterOptions(
        entry.displayMode === null ? detail : { ...detail, displayMode: entry.displayMode },
      );
      if (options.length > 0) bySlug[entry.slug] = options;
    }
    return { options: bySlug, pendingSlugs };
  }, [dictionaryFields, dictionaries]);

  return useMemo(
    () => ({
      userOptions,
      relationOptions,
      dictionaryOptions: dictionaryState.options,
      dictionaryPendingSlugs: dictionaryState.pendingSlugs,
    }),
    [userOptions, relationOptions, dictionaryState],
  );
}
